import { piniaInstance } from "../js/global";
import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { postData } from "../js/util/fetchhandler";
import { doneMessage, errorMessage } from '../js/util/notifications';

export default defineStore("cart", () => {
  const items = ref([]);
  const totalItems = ref(0);
  const totalCost = ref(0);

  // Lazy loading seguro de userStore - se importa solo cuando se necesita
  let _userStore = null;
  const getUserStore = () => {
    if (!_userStore) {
      const { useCurrentUserStore } = require("./current_user");
      _userStore = useCurrentUserStore;
    }
    return _userStore;
  };

  // Computed reactivo para loggedIn
  const loggedIn = computed(() => {
    return getUserStore().loggedIn;
  });

  // Computed para items ordenados alfabéticamente
  const sortedItems = computed(() => {
    return [...items.value].sort((a, b) => {
      return a.name.localeCompare(b.name, 'es-AR', { sensitivity: 'base' });
    });
  });

  // Helper para verificar autenticación
  const checkAuth = (showError = true) => {
    if (!loggedIn.value) {
      if (showError) {
        errorMessage("Debe iniciar sesión para realizar esta acción");
      }
      return false;
    }
    return true;
  };

  // Actualiza los totales del carrito
  const updateTotals = () => {
    totalItems.value = items.value.length;
    totalCost.value = items.value.reduce((accumulator, currentValue) => {
      return accumulator + currentValue.price * currentValue.qty;
    }, 0);
  };

  const getItems = async () => {
    if (!checkAuth(false)) return;

    let response = await services.shoppingcart.get();
    items.value = Object.values(response);
    updateTotals();
  };

  const addItem = async (data) => {
    let response = await services.shoppingcart.add(data);
    if (response.status) {
      doneMessage("Producto agregado al pedido");
      await getItems();
    } else {
      errorMessage("Ha ocurrrido un error. Reintente por favor");
    }
  };

  const updateItem = async (rowid, data) => {
    let response = await services.shoppingcart.update(rowid, data);
    if (response.status) {
      doneMessage("Pedido actualizado");
      await getItems();
    } else {
      errorMessage("Ha ocurrrido un error. Reintente por favor");
    }
  };

  const removeItem = async (rowid) => {
    let response = await services.shoppingcart.remove(rowid);
    if (response.status) {
      doneMessage("Producto eliminado del pedido");
      await getItems();
    } else {
      errorMessage("Ha ocurrrido un error. Reintente por favor");
    }
  };

  const clear = async () => {
    let response = await services.shoppingcart.clear();
    if (response.status) {
      await getItems();
    } else {
      errorMessage("Ha ocurrrido un error. Reintente por favor");
    }
  };

  const clearCartLocal = () => {
    items.value = [];
    totalItems.value = 0;
    totalCost.value = 0;
  };

  const createOrder = async (
    freeShipping = false,
    shippingMethod = "DELIVERY",
    chippingProvinceId = "",
    shippingCityId = "",
    shippingAddress = "",
    shippingZipCode = ""
  ) => {
    try {
      let dataOrder = {
        free_shipping: freeShipping,
        shipping_method: shippingMethod,
        province_id: chippingProvinceId,
        city_id: shippingCityId,
        address: shippingAddress,
        zip_code: shippingZipCode,
      };
      let result = await postData(
        `/api/orders/create`,
        dataOrder
      ).then((data) => {
        return data;
      });
      return result;
    } catch (error) {
      alert("Error al crear el pedido: " + error);
      console.log(error);
    }
  };

  /**
   * Llama al endpoint de la API para guardar el pedido.
   * Devuelve la respuesta de la API al componente que lo llamó.
   */
  const saveOrder = async (
    freeShipping = false,
    shippingMethod = "DELIVERY",
    shippingNotes = "",
    chippingProvinceId = "",
    shippingCityId = "",
    shippingAddress = "",
    shippingZipCode = ""
  ) => {
    try {
      let dataOrder = {
        free_shipping: freeShipping,
        shipping_method: shippingMethod,
        province_id: chippingProvinceId,
        city_id: shippingCityId,
        address: shippingAddress,
        zip_code: shippingZipCode,
        notes: shippingNotes,
      };
      let result = await postData(`/api/orders/save`, dataOrder).then((data) => data);
      return result;
    } catch (error) {
      console.error("Error en la acción saveOrder de la store:", error);
      return { status: false, message: error.message };
    }
  };

  // Watch reactivo: carga o limpia el carrito según autenticación
  watch(
    loggedIn,
    async (newValue, oldValue) => {
      //carga al cambio de false/true (autenticación)
      if (newValue && !oldValue) {
        await getItems();
      } 
      //limpia al cambio true/false (desautenticación)
      else if (!newValue && oldValue) {
        clearCartLocal();
      }
    },
    { immediate: true }
  );

  return {
    items,
    totalItems,
    totalCost,
    loggedIn,
    sortedItems,
    getItems,
    addItem,
    removeItem,
    updateItem,
    clear,
    createOrder,
    saveOrder
  };
})(piniaInstance);
